import { MISSING_UNITS_BODY_BYTES, MISSING_UNITS_MAX, type MissingUnit, type MissingUnits, type MissingUnitsRequest } from "@shyne/shared/api";
import { inBatches } from "@/repositories/unit-records";
import type { PlannedAppointmentRow } from "./bulk-upload-plan";

export interface CreateMissingUnitsResult {
  resolvedRows: PlannedAppointmentRow[];
  errors: string[];
}

export type CreateMissing = (request: MissingUnitsRequest) => Promise<MissingUnits>;

const BED_BATH_PATTERN = /(\d+)\s*x\s*(\d+)/;

export function inferBedBathFromServiceName(serviceName: string): { beds: number; baths: number } | null {
  const match = BED_BATH_PATTERN.exec(serviceName);
  return match ? { beds: Number(match[1]), baths: Number(match[2]) } : null;
}

interface UnitToCreate {
  property: string;
  unit_number: string;
  beds: number;
  baths: number;
}

function collectUnitsToCreate(rows: PlannedAppointmentRow[]): Map<string, UnitToCreate> {
  const units = new Map<string, UnitToCreate & { inferred: boolean }>();

  for (const row of rows) {
    const key = `${row.propertyId}|${row.unitNumber}`;
    const existing = units.get(key);
    if (existing?.inferred) continue;

    const inferred = inferBedBathFromServiceName(row.serviceName);
    if (existing && !inferred) continue;

    units.set(key, {
      property: row.propertyId,
      unit_number: row.unitNumber,
      beds: inferred?.beds ?? 0,
      baths: inferred?.baths ?? 0,
      inferred: Boolean(inferred),
    });
  }

  return new Map(
    [...units].map(([key, { property, unit_number, beds, baths }]) => [key, { property, unit_number, beds, baths }])
  );
}

const unitKey = (property: string, number: string) => `${property}|${number}`;

export const missingUnitBatches = (units: readonly MissingUnit[]): MissingUnit[][] =>
  inBatches(units, { count: MISSING_UNITS_MAX, bytes: MISSING_UNITS_BODY_BYTES, sizeOf: (unit) => JSON.stringify(unit).length + 1 });

export async function resolveMissingUnits(rows: PlannedAppointmentRow[], createMissing: CreateMissing): Promise<CreateMissingUnitsResult> {
  const uniqueUnits = [...collectUnitsToCreate(rows).values()].map(({ property, unit_number, beds, baths }) => ({ property, number: unit_number, beds, baths }));
  const unitIdByKey = new Map<string, string>();
  let failure: string | null = null;
  try {
    for (const batch of missingUnitBatches(uniqueUnits)) {
      const { items } = await createMissing({ units: batch });
      items.forEach((unit) => unitIdByKey.set(unitKey(unit.property, unit.number), unit.id));
    }
  } catch (error) {
    const left = uniqueUnits.filter((unit) => !unitIdByKey.has(unitKey(unit.property, unit.number))).length;
    failure = `Failed to create ${left} missing unit${left !== 1 ? "s" : ""}: ${error instanceof Error ? error.message : String(error)}`;
  }

  const resolved = rows.map((row) => ({
    ...row,
    unitId: unitIdByKey.get(unitKey(row.propertyId, row.unitNumber)) ?? null,
  }));
  const unresolved = resolved.filter((row) => row.unitId === null);

  return {
    resolvedRows: resolved.filter((row) => row.unitId !== null),
    errors: failure !== null ? [failure] : unresolved.map((row) => `Row ${row.rowNumber}: Unit "${row.unitNumber}" could not be created`),
  };
}
